// ============================================
// services/categories.ts
//
// 作品分类: 后端定义, 前端不写死.
//
// 来源: community-get-categories (后端读 community_config config_key="categories").
// 运维在后端改一条配置即可增删分类, 前端无需改代码、无需发版.
//
// 用法:
//   - App 启动时 (main.tsx) 调一次 loadCategories() 预热
//   - 组件用 getCategoriesSync() 拿当前列表 (含默认兜底, 永不为空)
//   - 用 categoryLabel(id) 把机器值翻成显示名
//
// 兜底: 接口未就绪 / 网络失败 / 空库 → 用 DEFAULT_CATEGORIES, 保证页面照常渲染.
// ============================================

import { callApi } from './api';

export interface Category {
  id: string;
  label: string;
  order?: number;
}

// 内置默认 (跟后端 DEFAULT_CATEGORIES 对齐). 仅兜底用.
export const DEFAULT_CATEGORIES: Category[] = [
  { id: 'smart_home', label: '智能家居', order: 1 },
  { id: 'car', label: '小车', order: 2 },
  { id: 'light', label: '灯效', order: 3 },
  { id: 'sound', label: '声音', order: 4 },
  { id: 'iot', label: 'IoT', order: 5 },
  { id: 'other', label: '其他', order: 99 },
];

// 模块级缓存
let _cache: Category[] = DEFAULT_CATEGORIES;
let _loaded = false;
let _inflight: Promise<Category[]> | null = null;

/** 拉取分类 (带并发去重 + 缓存). 失败时保留上一次 / 默认, 不抛异常. */
export async function loadCategories(force = false): Promise<Category[]> {
  if (_loaded && !force) return _cache;
  if (_inflight) return _inflight;

  _inflight = (async () => {
    const r = await callApi<{ categories: Category[] }>('community-get-categories', {}, { noAuth: true });
    if (r.ok && Array.isArray(r.categories) && r.categories.length > 0) {
      _cache = (r.categories as Category[])
        .filter((c) => c && typeof c.id === 'string' && c.id)
        .map((c) => ({ id: c.id, label: c.label || c.id, order: c.order ?? 999 }))
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      _loaded = true;
    }
    // 失败: 不动 _cache (保持默认或上次成功值), 也不标记 loaded, 下次还会重试
    _inflight = null;
    return _cache;
  })();

  return _inflight;
}

/** 同步拿当前分类 (永不为空, 至少是默认). */
export function getCategoriesSync(): Category[] {
  return _cache;
}

/** 机器值 → 显示名. 未知 id 原样返回 (不丢失数据). */
export function categoryLabel(id: string): string {
  const hit = _cache.find((c) => c.id === id);
  return hit ? hit.label : id;
}
